import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import {
	ProjectDescriptionEditor,
	ProjectDescriptionPreview,
} from "@/components/projects/project-description";
import { projectDescriptionText } from "@/components/projects/project-description-utils";
import { POPUP_ATTR } from "@/components/markdown-editor/ToolbarMenus";
import { cn } from "@/lib/utils";
import { reportError } from "./utils";

export function InlineDescription({
	value,
	canEdit,
	onSave,
}: {
	value: string;
	canEdit: boolean;
	onSave: (v: string) => Promise<void>;
}) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(value);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setDraft(value);
	}, [value]);

	async function commit() {
		if (saving) return;
		if (draft === value) {
			setEditing(false);
			return;
		}
		setSaving(true);
		try {
			await onSave(draft);
			setEditing(false);
		} catch (e) {
			reportError(e);
			setDraft(value);
			setEditing(false);
		} finally {
			setSaving(false);
		}
	}

	function cancel() {
		setDraft(value);
		setEditing(false);
	}

	const hasText = projectDescriptionText(value);

	if (!editing) {
		return (
			<div
				onClick={(e) => {
					// Links in the preview open; anywhere else enters edit mode.
					if (!canEdit || (e.target as HTMLElement).closest("a")) return;
					setEditing(true);
				}}
				className={cn(
					"break-words text-[14px] leading-5",
					canEdit &&
						"cursor-text rounded -mx-1 px-1 pb-1  hover:bg-muted-subtle/60 transition-colors",
				)}
			>
				{hasText ? (
					<ProjectDescriptionPreview value={value} className="text-foreground" />
				) : (
					<p className="text-muted italic">
						No description provided.
					</p>
				)}
			</div>
		);
	}

	return (
		<div
			className="relative"
			onKeyDown={(e) => {
				// Escape inside a toolbar menu only closes the menu.
				if (e.key !== "Escape") return;
				if ((e.target as HTMLElement).closest(`[${POPUP_ATTR}]`)) return;
				e.preventDefault();
				e.stopPropagation();
				cancel();
			}}
		>
			<ProjectDescriptionEditor
				autoFocus
				value={draft}
				onChange={setDraft}
				onBlur={commit}
				placeholder="Describe the task..."
			/>
			{saving && (
				<Loader2 className="absolute right-2 bottom-2.5 h-3.5 w-3.5 animate-spin text-muted" />
			)}
		</div>
	);
}
